import { Component, computed, inject, input } from '@angular/core';
import { UwIcon } from '../../ui/icon/uw-icon';
import { UwTabs, UwTabItem } from '../../ui/tabs/uw-tabs';
import { UwSkeleton } from '../../ui/skeleton/uw-skeleton';
import { UwEvidenceSource } from '../evidence-source/uw-evidence-source';
import { UwAuthorityEvidence } from '../evidence-authority/uw-authority-evidence';
import { UwPricingEvidence } from '../evidence-pricing/uw-pricing-evidence';
import { UwLossEvidence } from '../evidence-loss/uw-loss-evidence';
import { UwRiskEvidence } from '../evidence-risk/uw-risk-evidence';
import { UwEnrichmentEvidence } from '../evidence-enrichment/uw-enrichment-evidence';
import { EvidenceStore } from '../../../core/stores/evidence.store';
import type { EvidenceType } from '../../../core/models/evidence.model';

const TABS: UwTabItem[] = [
  { id: 'authority', label: 'Authority', icon: 'shield' },
  { id: 'pricing', label: 'Pricing', icon: 'calculator' },
  { id: 'loss', label: 'Loss', icon: 'file-text' },
  { id: 'risk', label: 'Risk', icon: 'gauge' },
  { id: 'enrichment', label: 'Enrichment', icon: 'database' },
];

@Component({
  selector: 'uw-evidence-panel',
  standalone: true,
  imports: [
    UwIcon,
    UwTabs,
    UwSkeleton,
    UwEvidenceSource,
    UwAuthorityEvidence,
    UwPricingEvidence,
    UwLossEvidence,
    UwRiskEvidence,
    UwEnrichmentEvidence,
  ],
  template: `
    <div class="flex h-full flex-col gap-3">
      <div class="flex items-center justify-between gap-2">
        <div class="flex items-center gap-1.5">
          <uw-icon name="search-check" [size]="15" class="text-ai" />
          <h2 class="typo-title text-primary">Evidence & Sources</h2>
        </div>
        <span class="rounded-full bg-surface-sunken px-2 py-0.5 text-[10px] font-bold text-secondary">{{ sourceCount() }}</span>
      </div>

      @if (evidence.activeSource()) {
        <uw-evidence-source />
      }

      <uw-tabs [tabs]="TABS" [active]="evidence.activeEvidence()" (activeChange)="selectEvidence($event)" />

      <div class="flex-1 overflow-y-auto pr-1">
        @if (loading()) {
          <div class="space-y-2.5">
            <uw-skeleton height="72px" />
            <uw-skeleton height="120px" />
            <uw-skeleton height="48px" />
          </div>
        } @else {
          @switch (evidence.activeEvidence()) {
            @case ('authority') {
              <uw-authority-evidence [authority]="evidence.data()?.authority ?? null" />
            }
            @case ('pricing') {
              <uw-pricing-evidence [pricing]="evidence.data()?.pricing ?? null" />
            }
            @case ('loss') {
              <uw-loss-evidence [loss]="evidence.data()?.loss ?? null" />
            }
            @case ('risk') {
              <uw-risk-evidence [risk]="evidence.data()?.risk ?? null" />
            }
            @case ('enrichment') {
              <uw-enrichment-evidence [enrichment]="evidence.data()?.enrichment ?? null" />
            }
          }
        }
      </div>
    </div>
  `,
})
export class UwEvidencePanel {
  protected readonly TABS = TABS;

  protected readonly evidence = inject(EvidenceStore);
  readonly loading = input(false);

  protected readonly sourceCount = computed(() => `${this.evidence.sources().length} sources`);

  protected selectEvidence(id: string): void {
    this.evidence.selectEvidence(id as EvidenceType);
  }
}
